import { BAOCustomElement } from '@bao/custom-element'
import { eventBus, EVENTS } from '@bao/events'
import { getElement, getElements, gracefullyReplaceNode } from '@bao/utils'

const selectors = {
  addToCartPanel: '[data-product-add-to-cart-panel]',
  addToCart: '[data-product-add-to-cart]',
  addToCartText: '[data-product-add-to-cart-text]',
  personalisationTrigger: '[data-product-personalisation-trigger]',
  backInStockPanel: '[data-product-back-in-stock-panel]',
  backInStockForm: '[data-product-back-in-stock-form]',
  badges: '[data-product-badges]',
  recommendations: '[data-product-recommendations]',
  media: '[data-product-media]',
  price: '[data-product-price]',
}

export class ProductInfo extends BAOCustomElement() {
  static get requiredElements () {
    return ['form']
  }

  static get requiredAttributes () {
    return ['data-section', 'data-product-info-id', 'data-url']
  }

  abortController = undefined
  pendingRequestUrl = undefined
  preReplaceProductOptionsCallbacks = []
  postReplacePageCallbacks = []

  checkValidity () {
    super.checkValidity()

    if (this.els.form.element.tagName !== 'PRODUCT-FORM') {
      this.throwError(`this.els.form.element should be a <product-form>`)
    }
  }

  setupListeners () {
    super.setupListeners()

    this.onProductOptionChange = this.onProductOptionChange.bind(this)
    this.onProductOptionEntered = this.onProductOptionEntered.bind(this)
    this.onProductFormSubmit = this.onProductFormSubmit.bind(this)

    this.productForm.addEventListener('submit', this.onProductFormSubmit, true)

    eventBus.addEventListener(
      EVENTS.PRODUCT.OPTION_SELECTED,
      this.onProductOptionChange,
    )
    eventBus.addEventListener(
      EVENTS.PRODUCT.OPTION_ENTERED,
      this.onProductOptionEntered,
    )
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    eventBus.removeEventListener(
      EVENTS.PRODUCT.OPTION_SELECTED,
      this.onProductOptionChange,
    )
    eventBus.removeEventListener(
      EVENTS.PRODUCT.OPTION_ENTERED,
      this.onProductOptionEntered,
    )

    this.productForm.removeEventListener(
      'submit',
      this.onProductFormSubmit,
      true,
    )
  }

  onProductFormSubmit (event) {
    if (!this.loading) return

    event.preventDefault()
    event.stopImmediatePropagation()
  }

  /**
   * @param {CustomEvent} payload
   * @param {Event} payload.detail.event
   * @param {string[]} payload.detail.selectedOptionValues
   */
  onProductOptionEntered ({ detail: { event, selectedOptionValues } }) {
    if (!this.contains(event.target)) return

    const optionTarget = event.target.querySelector('[data-option-value-id]')
    if (!optionTarget) return
    if (!optionTarget.dataset.optionValueId) return

    const name = optionTarget.getAttribute('name')
    const selectedOption = this.querySelector(
      `[name="${name}"][selected], [name="${name}"]:checked`,
    )
    if (!selectedOption) return
    if (selectedOption === optionTarget) return

    const selectedOptionId = selectedOption.dataset.optionValueId
    if (!selectedOptionId) return

    const newSelectedOptionValues = selectedOptionValues.map((value) => {
      if (value === selectedOptionId) return optionTarget.dataset.optionValueId

      return value
    })

    const productUrl = optionTarget.dataset.productUrl || this.dataset.url
    fetchProductData(
      this.buildRequestUrlWithParams(productUrl, newSelectedOptionValues),
    )
  }

  /**
   * @param {CustomEvent} payload
   * @param {Event} payload.detail.event
   * @param {HTMLOptionElement | EventTarget} payload.detail.target
   * @param {string[]} payload.detail.selectedOptionValues
   */
  async onProductOptionChange ({
    detail: { event, target, selectedOptionValues },
  }) {
    if (!this.contains(target)) return
    if (this.dataset.productInfoId !== target.dataset.productInfoId) return

    this.loading = true

    const productUrl =
      target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url
    this.pendingRequestUrl = productUrl
    const shouldSwapProduct = this.dataset.url !== productUrl
    const shouldFetchFullPage =
      this.dataset.updateUrl === 'true' && shouldSwapProduct

    await this.fetchProductDetails({
      requestUrl: this.buildRequestUrlWithParams(
        productUrl,
        selectedOptionValues,
        shouldFetchFullPage,
      ),
      targetId: target.id,
      callback: shouldSwapProduct
        ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
        : this.renderProductDetails(productUrl),
    })
  }

  /**
   * @callback fetchProductDetailsCallback
   * @param {Document} html
   */

  /**
   * @param {Object} payload
   * @param {string} payload.requestUrl
   * @param {string} payload.targetId
   * @param {fetchProductDetailsCallback} payload.callback
   */
  async fetchProductDetails ({ requestUrl, targetId, callback }) {
    this.abortController?.abort()

    const abortController = new AbortController()
    this.abortController = abortController

    try {
      const text = await fetchProductData(requestUrl, abortController.signal)

      if (this.abortController !== abortController) return

      this.pendingRequestUrl = undefined

      const html = new DOMParser().parseFromString(text, 'text/html')

      callback(html)

      getElement(`#${targetId}`)?.focus()
    } catch (e) {
      if (e.name === 'AbortError') {
        console.info('Fetch aborted by user')
      } else {
        console.error(e)

        if (this.abortController === abortController) {
          this.pendingRequestUrl = undefined
          this.loading = false
        }
      }
    }
  }

  /**
   * @param {string} productUrl
   * @param {boolean} updateFullPage
   */
  handleSwapProduct (productUrl, updateFullPage = false) {
    /**
     * @param {Document} html
     */
    return (html) => {
      const variant = this.getSelectedVariant(html)

      this.updateURL(productUrl, variant?.id)

      if (updateFullPage) {
        document.querySelector('head title').innerHTML =
          html.querySelector('head title').innerHTML

        gracefullyReplaceNode(
          document.querySelector('main'),
          html.querySelector('main'),
          this.preReplaceProductOptionsCallbacks,
          this.postReplacePageCallbacks,
        )
      } else {
        gracefullyReplaceNode(
          this,
          html.querySelector('product-info'),
          this.preReplaceProductOptionsCallbacks,
          this.postReplacePageCallbacks,
        )
      }

      this.updateReviewRating()
      this.updateStoq()

      const prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      ).matches

      window.scrollTo({
        top: 0,
        left: 0,
        behavior: prefersReducedMotion ? 'instant' : 'smooth',
      })
    }
  }

  updateReviewRating () {
    window.ratingSnippet('ruk_rating_snippet', {
      store: window.reviewsIoStore,
      color: '#041e42',
      linebreak: false,
      lang: window.reviewsIoLang,
      showEmptyStars: true,
      enableSyndication: window.reviewsIoEnableSyndication,
    })
  }

  updateStoq () {
    window._RestockRocket.updateAddToCartForms(this.els.form.element)
  }

  /**
   * @param {string} productUrl
   */
  renderProductDetails (productUrl) {
    /**
     * @param {Document} html
     */
    return (html) => {
      const variant = this.getSelectedVariant(html)

      if (!variant) {
        this.updateAvailability(false, window.theme.strings.unavailable)

        this.loading = false

        return
      }

      this.updateOptionValues(html)
      this.updateURL(productUrl, variant?.id)
      this.updateVariantInputs(variant?.id)

      /**
       * @callback PostReplaceCallback
       * @param {Element} currentElement
       * @param {Element} newElement
       */

      /**
       * @param {string} selector
       * @param {PostReplaceCallback} [postReplaceCallback]
       */
      const updateCurrentPageFromFetchedData = (
        selector,
        postReplaceCallback,
      ) => {
        const newElement = html.querySelector(selector)
        const currentElements = getElements(selector)

        if (newElement && currentElements) {
          currentElements.forEach((currentElement) => {
            if (currentElement.innerHTML !== newElement.innerHTML) {
              currentElement.innerHTML = newElement.innerHTML
            }
          })

          if (typeof postReplaceCallback === 'function') {
            postReplaceCallback(currentElements, newElement)
          }
        }
      }

      updateCurrentPageFromFetchedData(
        `[data-product-info-id="${this.productInfoId}"]${selectors.badges}`,
      )
      updateCurrentPageFromFetchedData(
        `[data-product-info-id="${this.productInfoId}"]${selectors.media}`,
      )
      updateCurrentPageFromFetchedData(
        `[data-product-info-id="${this.productInfoId}"]${selectors.price}`,
      )
      updateCurrentPageFromFetchedData(
        `[data-product-info-id="${this.productInfoId}"]${selectors.recommendations}`,
      )

      const productIsAvailable = !html
        .querySelector('product-form button[type=submit]')
        ?.hasAttribute('disabled')

      this.updateAvailability(productIsAvailable, '', variant)

      eventBus.dispatchEvent(EVENTS.PRODUCT.OPTION_CHANGED, {
        sectionId: this.sectionId,
        html,
        variant,
      })

      this.loading = false
    }
  }

  updateAvailability (productIsAvailable, text, variant) {
    const addToCartPanelEls = getElements(
      `[data-product-info-id="${this.productInfoId}"]${selectors.addToCartPanel}`,
    )
    const addToCartEls = getElements(
      `[data-product-info-id="${this.productInfoId}"]${selectors.addToCart}`,
    )
    const addToCartTextEls = getElements(
      `[data-product-info-id="${this.productInfoId}"]${selectors.addToCartText}`,
    )

    const personalisationTriggerEls = getElements(
      `[data-product-info-id="${this.productInfoId}"]${selectors.personalisationTrigger}`,
    )

    const backInStockPanelEls = getElements(
      `[data-product-info-id="${this.productInfoId}"]${selectors.backInStockPanel}`,
    )
    const backInStockFormEls = getElements(
      `[data-product-info-id="${this.productInfoId}"]${selectors.backInStockForm}`,
    )

    const recommendationEls = getElements(
      `[data-product-info-id="${this.productInfoId}"]${selectors.recommendations}`,
    )

    addToCartPanelEls.forEach((addToCartPanelEl) => {
      addToCartPanelEl.setAttribute('aria-hidden', !productIsAvailable)
    })
    addToCartEls.forEach((addToCartEl) => {
      addToCartEl.toggleAttribute('disabled', !productIsAvailable)
    })

    addToCartTextEls.forEach((addToCartTextEl) => {
      addToCartTextEl.textContent =
        text || productIsAvailable
          ? window.theme.strings.addToCart
          : window.theme.strings.soldOut
    })

    personalisationTriggerEls.forEach((personalisationTriggerEl) => {
      personalisationTriggerEl.setAttribute('data-variant-id', variant?.id)
    })

    backInStockPanelEls.forEach((backInStockPanelEl) => {
      backInStockPanelEl.setAttribute('aria-hidden', productIsAvailable)
    })

    backInStockFormEls.forEach((backInStockFormEl) => {
      backInStockFormEl.toggleAttribute('disabled', productIsAvailable)
      backInStockFormEl.setAttribute('data-variant-id', variant?.id)
    })

    recommendationEls.forEach((recommendationEl) => {
      recommendationEl.setAttribute('aria-hidden', productIsAvailable)
    })

    this.dataset.available = productIsAvailable
  }

  /**
   * @param {Document} html
   */
  updateOptionValues (html) {
    const productOptions = html.querySelector(
      `${this.productOptions.tagName.toLowerCase()}`,
    )

    if (productOptions) {
      gracefullyReplaceNode(
        this.productOptions,
        productOptions,
        this.preReplaceProductOptionsCallbacks,
      )
    }
  }

  /**
   * @param {string} url
   * @param {string} [variantId]
   */
  updateURL (url, variantId) {
    if (this.dataset.updateUrl === 'false') return

    const newUrl = `${url}${variantId ? `?variant=${variantId}` : ''}`

    window.history.replaceState({}, '', newUrl)

    const shareButtons = getElements('share-button')

    if (!shareButtons) return

    shareButtons.forEach((shareButton) => {
      shareButton.updateUrl(newUrl)
    })
  }

  /**
   * @param {string} [variantId]
   */
  updateVariantInputs (variantId) {
    if (!variantId) return

    const input = this.productForm.querySelector('input[name="id"]')
    if (!input) return

    input.value = variantId
    input.dispatchEvent(new Event('change', { bubbles: true }))
  }

  /**
   * @param {string} url
   * @param {string[]} optionValues
   * @param {boolean} shouldFetchFullPage
   */
  buildRequestUrlWithParams (url, optionValues, shouldFetchFullPage = false) {
    const params = []

    !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`)

    if (optionValues.length) {
      params.push(`option_values=${optionValues.join(',')}`)
    }

    return `${url}?${params.join('&')}`
  }

  /**
   * @param {Document} productInfoNode
   *
   * @returns {ShopifyVariant | null}
   */
  getSelectedVariant (productInfoNode) {
    const selectedVariantDataEl = productInfoNode.querySelector(
      `${this.productOptions.tagName.toLowerCase()} [${this.productOptions._elIdentifier}="selected-variant"]`,
    )
    if (!selectedVariantDataEl) return null

    return JSON.parse(selectedVariantDataEl.innerHTML)
  }

  get productForm () {
    return this.els.form.element
  }

  get productInfoId () {
    return this.dataset.productInfoId
  }

  get productOptions () {
    return this.els.options.element
  }

  get sectionId () {
    return this.dataset.originalSection || this.dataset.section
  }

  get loading () {
    return this.getAttribute('aria-busy') === 'true'
  }

  set loading (val) {
    this.setAttribute('aria-busy', val.toString())
    this.updateAddToCartLoadingState(val)
  }

  updateAddToCartLoadingState (isLoading) {
    const addToCartEls = getElements(
      `[data-product-info-id="${this.productInfoId}"]${selectors.addToCart}`,
    )

    addToCartEls.forEach((addToCartEl) => {
      if (isLoading) {
        if (!addToCartEl.hasAttribute('data-product-loading')) {
          addToCartEl.dataset.productLoadingWasDisabled = addToCartEl
            .hasAttribute('disabled')
            .toString()
        }

        addToCartEl.setAttribute('data-product-loading', '')
        addToCartEl.setAttribute('disabled', '')

        return
      }

      const wasDisabled =
        addToCartEl.dataset.productLoadingWasDisabled === 'true'

      addToCartEl.removeAttribute('data-product-loading')
      delete addToCartEl.dataset.productLoadingWasDisabled

      if (this.dataset.available === 'true') {
        addToCartEl.removeAttribute('disabled')
      } else if (this.dataset.available === 'false') {
        addToCartEl.setAttribute('disabled', '')
      } else {
        addToCartEl.toggleAttribute('disabled', wasDisabled)
      }
    })
  }
}

if (!customElements.get('product-info')) {
  customElements.define('product-info', ProductInfo)
}

class ProductInfoCache {
  constructor () {
    /** @type {Map<String, String>} */
    this.cache = new Map()
  }

  has (key) {
    return this.cache.has(key)
  }

  /**
   * @param {String} key
   * @param {String} value
   */
  set (key, value) {
    this.cache.set(key, value)
  }

  /**
   * @param {String} key
   */
  get (key) {
    return this.cache.get(key)
  }

  delete (key) {
    this.cache.delete(key)
  }
}

const cache = new ProductInfoCache()

/**
 * @param {string} requestUrl
 * @param {AbortSignal} abortSignal
 */
async function fetchProductData (requestUrl, abortSignal = undefined) {
  if (cache.has(requestUrl)) {
    return cache.get(requestUrl)
  }

  const response = await fetch(requestUrl, { signal: abortSignal })
  const text = await response.text()

  cache.set(requestUrl, text)

  return text
}
